// Self-hosted fonts (QZ-D47): no request to Google, and they work offline in local dev.
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/outfit/600.css';
import '@fontsource/outfit/700.css';
import '@fontsource/outfit/800.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/600.css';
import './styles/main.scss';
import './loc/i18n';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router';
import { App } from './App';
import { getWebConfig, loadWebConfig } from './config/loadWebConfig';
import { store } from './components/store/AppStore';
import { setAccessTokenProvider, setUnauthorizedHandler } from './components/api/quizdbApi';
import { type AuthServices, AuthServicesContext } from './components/auth/authServices';
import { createCognitoAuthClient } from './components/auth/cognitoAuthClient';
import { createSessionManager } from './components/auth/sessionManager';

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('#root element not found');
}
const root = createRoot(rootElement);

const render = (services: AuthServices) => {
  root.render(
    <StrictMode>
      <Provider store={store}>
        <AuthServicesContext.Provider value={services}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </AuthServicesContext.Provider>
      </Provider>
    </StrictMode>
  );
};

const renderError = (error: unknown) => {
  // eslint-disable-next-line no-console
  console.error('Failed to load web config', error);
  root.render(<p>Failed to load configuration. Please try again later.</p>);
};

/**
 * Builds the real auth services and restores the session once, before the
 * first render (QZ-D29): the access token lives only in memory, so a reload
 * gets a new one from the API's session cookie. A failed restore is not an
 * error; it just leaves the app signed out.
 */
const startApp = async (): Promise<AuthServices> => {
  const cognito = createCognitoAuthClient(getWebConfig().auth);
  const session = createSessionManager(store);
  setAccessTokenProvider(() => session.getAccessToken());
  setUnauthorizedHandler(() => session.refresh());
  await session.restore();
  return { cognito, session };
};

// This is the only place config is loaded (QZ-D10). No top-level await:
// Vite's default build target rejects it.
loadWebConfig().then(startApp).then(render, renderError);
